import _ from 'lodash';
import fs from 'fs-extra';
import path from 'path';
import { log } from '@swiper/core';
import { generateId } from '../utils';
import config from '~/config';
import Promise from 'bluebird';
import { Service } from './Service';
import glob from 'fast-glob';
import { SessionStore } from './SessionStore';

export class SessionsService extends Service {

  get name() { return 'sessions'; }
  get extension() { return '.ndjson'; }

  constructor( opts ) {
    super( opts );
    if ( ! this.dir ) this.dir = config.dir( 'sessions' );
  }

  getStore( id ) {
    return new SessionStore( { dir : this.dir, id, app : this.app } );
  }
  async setup( ...args ) {
    super.setup( ...args );
    await fs.ensureDir( this.activedir );
    await fs.ensureDir( this.closeddir );
  }

  async clear() {
    console.log( 'CLEARING SESSIONS', this.dir );
    if ( ! this.dir.includes( '/data/sessions' ) ) { // little safety net
      throw new Error( `Refusing to clear non-session directory ${this.dir}` );
    }
    return fs.emptyDir( this.dir );
  }

  async list( type='active' ) {
    const dir = this.pathto( type, false );
    if ( ! await fs.pathExists( dir ) ) return [];
    return Promise.map( fs.readdir( dir ), file => {
      return path.basename( file, this.extension );
    } );
  }

  get activedir() { return this.pathto( 'active', false ); }
  get closeddir() { return this.pathto( 'closed', false ); }

  async active() { return this.list( 'active' ); }
  async closed() { return this.list( 'closed' ); }

  async get( id, params ) {
    const opts = _.pick( params.query, 'events', 'strict' );
    return this.getStore( id ).loadSession( opts );
  }

  async find( { query : query_in, ...params } ) {
    const { active, closed, ...query } = _.defaults( query_in, {
      active  : true,
      closed  : false,
    } );
    // TODO - Speed this up by turning the query into things that can
    // be searched for with grep, then grep the files and only load
    // the ones that match for further testing.
    const ids = [];
    if ( active ) {
      const activeIds = await this.list();
      ids.push( ...activeIds );
    }
    if ( closed ) {
      const closedIds = await this.closed();
      ids.push( ...closedIds );
    }

    const matches = _.iteratee( query );
    return Promise.map( ids, async id => {
      const data = this.get( id );
      if ( matches( data ) ) return data;
    } ).filter( Boolean );
  }

  // This is actually a "find-or-create active" method, since there
  // can only be one active session for a membership.
  async create( data={}, params ) {
    const { membershipId, swipeId, ...extra } = data;
    if ( ! membershipId ) {
      this.throw( 'BadRequest', `Cannot create session without membershipId` );
    }
    if ( ! _.isEmpty( extra ) ) {
      const keys = _.keys( extra ).join( ', ' );
      this.throw(
        'BadRequest',
        `Unknown arguments to Session.create: ${keys}`,
      );
    }
    const active = await this.findForMembershipId( membershipId );
    if ( active ) return this.get( active, params );
    const id = generateId( [ 'session', membershipId ] );
    const store = this.getStore( id );
    await store.create();
    params.eventlog = {
      event         : 'session.opened',
      sessionId     : id,
      membershipId, swipeId,
    };

    return store.loadSession( _.pick( params.query, 'events', 'strict' ) );
  }

  async listForMembershipId( membershipId, type='active' ) {
    const gl = '*' + this.extension;
    const pattern = [ 'session', membershipId, gl ].join( '@' );
    log.debug( 'LOOKING FOR', pattern );
    const matches = await glob( pattern, {
      cwd       : this.pathto( type, false ),
      deep      : false,
      onlyFiles : true,
      globstar  : false,
      extglob   : false,
    } );
    return matches.sort().map( m => path.basename( m, this.extension ) );
  }

  async findForMembershipId( membershipId, type='active' ) {
    const ids = await this.listForMembershipId( membershipId, type );
    log.debug( 'MATCHES', ids );
    return _.last( ids );
  }

  async patch( id, event, params ) {
    if ( ! id ) {
      this.throw( 'BadRequest', `Cannot update session without id` );
    }
    if ( ! event.event ) {
      this.throw( 'BadRequest', `Cannot update session ${id} without event` );
    }
    const store = this.getStore( id );
    log.debug( 'SESSIONID', id );
    if ( ! await store.isActive() ) {
      this.throw( 'BadRequest', `Cannot update closed session ${id}` );
    }
    log.debug( 'SESSION UPDATE', id, event );

    await store.write( event );

    const opts = _.pick( params.query, 'events', 'strict' );
    const session = await store.loadSession( opts );

    // event types: {member,family,friend,guest}-{in,out}
    const [ who, what ] = event.event.split( '-' );
    // This should trigger eventlog entries for:
    //  session.{member,family,friend,guest}-{in,out}
    //  {guest,member}.{arrived,departed}

    const labels = {
      guest   : 'guest',
      member  : 'member',
      friend  : 'member',
      family  : 'member',
      in      : 'arrived',
      out     : 'departed',
    };

    params.eventlog = [
      {
        ...event,
        event     : [ 'session', event.event ].join( '.' ),
      },
      {
        ...event,
        event     : [ labels[ who ], labels[ what ] ].join( '.' ),
        via       : event.event,
      },
    ].map( x => _.defaults( x, {
      swipeId   : event.swipeId,
      sessionId : id,
    } ) );
    return session;
  }

  async remove( id, params ) {
    if ( ( id === null ) && this.isNotProd() && _.isEmpty( params.query ) ) {
      await this.clear();
      return 'OK';
    } else {
      this.throw( 'NotImplemented' );
    }
  }

}

export const hooks = {
  before  : { all : [ 'auth' ] },
  after   : { patch : [ 'eventlog' ] },
};
